import React, { useEffect, useRef, useState, useMemo } from "react";
import ReactHTMLTableToExcel from "react-html-table-to-excel";
import {
  FiAward,
  FiList,
  FiDownload,
  FiSearch,
  FiChevronDown,
  FiChevronUp,
  FiArrowUp,
  FiArrowDown,
  FiUsers,
  FiTrendingUp,
  FiTrendingDown,
  FiMinus,
} from "react-icons/fi";
import moment from "moment";
import { useTranslation } from "react-i18next";

const cleanHeadingTitle = (rawHeading = "", t,) => {
  if (!rawHeading) return t("Performance Leaderboard");
  return t(rawHeading);
};

const WaiterTableListWidgetV2 = ({ data, heading, date, size = "col-12" }) => {
  const { t } = useTranslation();
  const [loadAll, setLoadAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("desc"); // 'asc' | 'desc'
  const componentRef = useRef();

  const formattedHeading = useMemo(() => cleanHeadingTitle(heading, t), [heading, t]);

  const isLeaderboard = useMemo(
    () => heading.toLowerCase().includes("leaderboard") || heading.toLowerCase().includes("top"),
    [heading]
  );

  const startDateStr = useMemo(() => {
    if (!date || !date.startDate) return "";
    return typeof date.startDate === "object"
      ? moment(date.startDate).format("YYYY-MM-DD")
      : String(date.startDate);
  }, [date]);

  const endDateStr = useMemo(() => {
    if (!date || !date.endDate) return "";
    return typeof date.endDate === "object"
      ? moment(date.endDate).format("YYYY-MM-DD")
      : String(date.endDate);
  }, [date]);

  // Extract Columns
  const columns = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) return [];
    return Object.keys(data[0]);
  }, [data]);

  // Filter & Sort Data
  const processedData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];

    let filtered = [...data];

    // Search Filter
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter((row) =>
        Object.values(row).some((val) =>
          val !== null && val !== undefined && String(val).toLowerCase().includes(q)
        )
      );
    }

    // Sort
    if (sortColumn) {
      filtered.sort((a, b) => {
        const valA = a[sortColumn];
        const valB = b[sortColumn];
        const numA = parseFloat(valA);
        const numB = parseFloat(valB);

        if (!isNaN(numA) && !isNaN(numB)) {
          return sortDirection === "asc" ? numA - numB : numB - numA;
        }

        const strA = String(valA || "").toLowerCase();
        const strB = String(valB || "").toLowerCase();
        return sortDirection === "asc" ? strA.localeCompare(strB) : strB.localeCompare(strA);
      });
    }

    return filtered;
  }, [data, searchTerm, sortColumn, sortDirection]);

  // Displayed Rows (Top 5 or All)
  const displayedRows = useMemo(() => {
    if (loadAll) return processedData;
    return processedData.slice(0, 5);
  }, [processedData, loadAll]);

  const handleSort = (colKey) => {
    if (sortColumn === colKey) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(colKey);
      setSortDirection("desc");
    }
  };

  const triggerDownload = () => {
    if (componentRef.current) {
      componentRef.current.handleDownload();
    }
  };

  if (!data || columns.length === 0) {
    return (
      <div className={`waiter-v2-chart-card mb-3`}>
        <div className="waiter-v2-card-header">
          <div className="d-flex align-items-center gap-2">
            <div className="waiter-v2-icon-box blue">
              <FiList size={16} />
            </div>
            <div>
              <h6 className="waiter-v2-chart-title mb-0">{formattedHeading}</h6>
              <span className="waiter-v2-chart-sub">{t("Performance Records")}</span>
            </div>
          </div>
        </div>
        <div className="waiter-v2-empty-chart">
          <FiUsers size={24} color="#94a3b8" />
          <span>{t("No Records Found")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="waiter-v2-chart-card">
      {/* Hidden Excel Generator Button */}
      <ReactHTMLTableToExcel
        id={`table-xls-${heading.replace(/\s+/g, "-")}`}
        className="d-none"
        table={heading}
        filename={heading}
        sheet="Report Data"
        buttonText="Download XLS"
        ref={componentRef}
      />

      {/* Header Bar */}
      <div className="waiter-v2-card-header flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2">
          <div className={`waiter-v2-icon-box ${isLeaderboard ? "amber" : "blue"}`}>
            {isLeaderboard ? <FiAward size={16} /> : <FiList size={16} />}
          </div>
          <div>
            <div className="d-flex align-items-center gap-2">
              <h6 className="waiter-v2-chart-title mb-0">{formattedHeading}</h6>
              {isLeaderboard && (
                <span className="waiter-v2-badge-share">
                  <FiTrendingUp size={11} className="mr-1" />
                  {t("Active Ranking")}
                </span>
              )}
            </div>
            <span className="waiter-v2-chart-sub">
              {processedData.length} {t("Total Records")}
            </span>
          </div>
        </div>

        {/* Toolbar Controls: Search, Download & Load Toggle */}
        <div className="d-flex align-items-center gap-2 flex-wrap">
          {/* Search Filter */}
          {data.length > 3 && (
            <div className="waiter-v2-search-box">
              <FiSearch size={13} className="waiter-v2-search-icon" />
              <input
                type="text"
                className="waiter-v2-search-input"
                placeholder={t("Search...")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          )}

          {/* Load More/Less Toggle Pill */}
          {data.length > 5 && (
            <button
              type="button"
              className="waiter-v2-btn-secondary"
              onClick={() => setLoadAll(!loadAll)}
            >
              {loadAll ? (
                <>
                  {t("Show Less")} <FiChevronUp size={14} />
                </>
              ) : (
                <>
                  {t("View All")} ({data.length}) <FiChevronDown size={14} />
                </>
              )}
            </button>
          )}

          {/* Download XLS Button */}
          <button
            type="button"
            className="waiter-v2-btn-primary"
            onClick={triggerDownload}
            title={t("Download Excel Sheet")}
          >
            <FiDownload size={13} />
            <span>{t("Export")}</span>
          </button>
        </div>
      </div>

      {/* Table Canvas */}
      <div className="waiter-v2-table-wrapper">
        <table id={heading} className="waiter-v2-table">
          {startDateStr && endDateStr && (
            <thead className="d-none">
              <tr>
                <th colSpan={columns.length}>
                  {heading} ({startDateStr} - {endDateStr})
                </th>
              </tr>
            </thead>
          )}
          <thead>
            <tr>
              {/* Rank Column Header for Leaderboards */}
              {isLeaderboard && <th className="text-center" style={{ width: "50px" }}>#</th>}

              {columns.map((col, idx) => (
                <th
                  key={idx}
                  onClick={() => handleSort(col)}
                  className="cursor-pointer select-none"
                >
                  <div className="d-flex align-items-center gap-1 justify-content-between">
                    <span>{col}</span>
                    {sortColumn === col ? (
                      sortDirection === "asc" ? (
                        <FiArrowUp size={12} color="#2563eb" />
                      ) : (
                        <FiArrowDown size={12} color="#2563eb" />
                      )
                    ) : (
                      <span className="waiter-v2-sort-placeholder">↕</span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayedRows.length > 0 ? (
              displayedRows.map((row, rowIdx) => (
                <tr key={rowIdx}>
                  {/* Rank Badge Column */}
                  {isLeaderboard && (
                    <td className="text-center font-weight-bold">
                      {rowIdx === 0 ? (
                        <span className="waiter-v2-rank-badge gold">1</span>
                      ) : rowIdx === 1 ? (
                        <span className="waiter-v2-rank-badge silver">2</span>
                      ) : rowIdx === 2 ? (
                        <span className="waiter-v2-rank-badge bronze">3</span>
                      ) : (
                        <span className="waiter-v2-rank-badge default">{rowIdx + 1}</span>
                      )}
                    </td>
                  )}

                  {columns.map((col, colIdx) => {
                    const rawVal = row[col];
                    const numVal = parseFloat(rawVal);
                    const isValidNum = rawVal !== null && rawVal !== "" && rawVal !== "-" && !isNaN(numVal);
                    const formatted = isValidNum
                      ? numVal % 1 === 0
                        ? numVal.toLocaleString()
                        : numVal.toFixed(2)
                      : rawVal || "-";

                    const isCancelCol =
                      col.toLowerCase().includes("cancel") ||
                      col.toLowerCase().includes("cancelled");

                    const isNameCol =
                      colIdx === 0 ||
                      col.toLowerCase().includes("name") ||
                      col.toLowerCase().includes("waiter") ||
                      col.toLowerCase().includes("user");

                    const isGrowthCol =
                      col.toLowerCase().includes("growth") ||
                      col.toLowerCase().includes("trend") ||
                      col.toLowerCase().includes("change") ||
                      col.toLowerCase().includes("%") ||
                      (typeof rawVal === "string" && rawVal.includes("%"));

                    const isSalesCol =
                      !isGrowthCol && (
                        col.toLowerCase().includes("sales") ||
                        col.toLowerCase().includes("amount") ||
                        col.toLowerCase().includes("total") ||
                        col.toLowerCase().includes("revenue")
                      );

                    if (isNameCol) {
                      const nameStr = String(rawVal || "Staff");
                      const initial = nameStr.charAt(0).toUpperCase();

                      return (
                        <td key={colIdx}>
                          <div className="d-flex align-items-center gap-2">
                            <span className={`waiter-v2-avatar-chip avatar-bg-${(rowIdx % 5) + 1}`}>
                              {initial}
                            </span>
                            <span className="font-weight-600 color-slate">{nameStr}</span>
                            {rowIdx === 0 && isLeaderboard && (
                              <span className="waiter-v2-top-star" title={t("Top Performer")}>
                                ★
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    }

                    if (isGrowthCol) {
                      const cleanStr = String(rawVal ?? "").replace(/[%+]/g, "").trim();
                      const parsedGrowth = parseFloat(cleanStr);
                      const isValidGrowth = !isNaN(parsedGrowth) && rawVal !== null && rawVal !== "" && rawVal !== "-";

                      if (isValidGrowth) {
                        const isInverseMetric =
                          col.toLowerCase().includes("cancel") ||
                          col.toLowerCase().includes("refund");

                        const isPositiveChange = parsedGrowth > 0;
                        const isNegativeChange = parsedGrowth < 0;

                        // Trend icon indicates actual directional movement (Up for increase, Down for decrease)
                        const showUpTrend = isPositiveChange;
                        const showDownTrend = isNegativeChange;

                        // Color condition: For cancel/refund inverse metrics, increase is danger/red and decrease is success/green
                        const isDangerColor = isInverseMetric ? isPositiveChange : isNegativeChange;
                        const isSuccessColor = isInverseMetric ? isNegativeChange : isPositiveChange;

                        const absGrowth = Math.abs(parsedGrowth);
                        const formattedGrowth = `${
                          absGrowth % 1 === 0 ? absGrowth.toFixed(0) : absGrowth.toFixed(1)
                        }%`;

                        const badgeClass = isSuccessColor
                          ? "waiter-v2-growth-chip positive"
                          : isDangerColor
                          ? "waiter-v2-growth-chip negative"
                          : "waiter-v2-growth-chip neutral";

                        return (
                          <td key={colIdx}>
                            <span className={badgeClass}>
                              {showUpTrend && <FiTrendingUp size={13} />}
                              {showDownTrend && <FiTrendingDown size={13} />}
                              {!showUpTrend && !showDownTrend && <FiMinus size={13} />}
                              <span>{formattedGrowth}</span>
                            </span>
                          </td>
                        );
                      }
                    }

                    if (isCancelCol && isValidNum) {
                      return (
                        <td key={colIdx}>
                          <span className="waiter-v2-danger-chip">
                            {formatted}
                          </span>
                        </td>
                      );
                    }

                    if (isSalesCol && isValidNum) {
                      return (
                        <td key={colIdx}>
                          <span className="waiter-v2-sales-chip">
                            {formatted}
                          </span>
                        </td>
                      );
                    }

                    return (
                      <td key={colIdx}>
                        {formatted}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length + (isLeaderboard ? 1 : 0)} className="text-center py-4 text-muted">
                  {t("No matching records found")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WaiterTableListWidgetV2;